import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

type Point = { x: number; y: number };
type Direction = "UP" | "DOWN" | "LEFT" | "RIGHT";
type GameMode = "INFINITE" | "WALL";

// 2:3 Aspect Ratio Grid
const GRID_W = 20;
const GRID_H = 30;
const CELL_SIZE = 20;

const VIRTUAL_WIDTH = GRID_W * CELL_SIZE;
const VIRTUAL_HEIGHT = GRID_H * CELL_SIZE;

// Dynamic Speed configuration
const INITIAL_SPEED_DELAY = 180;
const MIN_SPEED_DELAY = 60;

const generateFood = (snake: Point[]): Point => {
  while (true) {
    const newFood = {
      x: Math.floor(Math.random() * GRID_W),
      y: Math.floor(Math.random() * GRID_H),
    };
    let isOnSnake = false;
    for (let i = 0; i < snake.length; i++) {
      if (snake[i].x === newFood.x && snake[i].y === newFood.y) {
        isOnSnake = true;
        break;
      }
    }
    if (!isOnSnake) return newFood;
  }
};

class AudioManager {
  private ctx: AudioContext | null = null;
  init() {
    if (!this.ctx) {
      this.ctx = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
  }
  playTone(freq: number, type: OscillatorType, duration: number) {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    gain.gain.setValueAtTime(0.1, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      0.001,
      this.ctx.currentTime + duration,
    );
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }
  playEatSound() {
    this.playTone(880, "sine", 0.1);
    setTimeout(() => this.playTone(1100, "sine", 0.1), 50);
  }
  playGameOverSound() {
    this.playTone(150, "sawtooth", 0.4);
  }
}

const audio = new AudioManager();

const App: React.FC = () => {
  const [snake, setSnake] = useState<Point[]>([{ x: 10, y: 15 }]);
  const [direction, setDirection] = useState<Direction>("UP");
  const [food, setFood] = useState<Point>({ x: 10, y: 10 });
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [score, setScore] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [mode, setMode] = useState<GameMode>("INFINITE");
  const [activeKey, setActiveKey] = useState<Direction | null>(null);

  const currentDirectionRef = useRef<Direction>("UP");
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const currentDelay = Math.max(
    MIN_SPEED_DELAY,
    INITIAL_SPEED_DELAY - Math.floor(score / 10) * 4,
  );

  useEffect(() => {
    currentDirectionRef.current = direction;
  }, [direction]);

  const startGame = (selectedMode: GameMode) => {
    audio.init();
    setMode(selectedMode);

    const cx = Math.floor(GRID_W / 2);
    const cy = Math.floor(GRID_H / 2);

    const initialSnake = [
      { x: cx, y: cy },
      { x: cx, y: cy + 1 },
      { x: cx, y: cy + 2 },
    ];

    setSnake(initialSnake);
    setDirection("UP");
    currentDirectionRef.current = "UP";
    setFood(generateFood(initialSnake));
    setGameOver(false);
    setScore(0);
    setIsPlaying(true);
  };

  const resignGame = () => {
    setGameOver(true);
    setIsPlaying(false);
    audio.playGameOverSound();
  };

  const changeDirection = useCallback(
    (newDir: Direction) => {
      if (gameOver || !isPlaying) return;
      const currentDir = currentDirectionRef.current;
      if (newDir === "UP" && currentDir !== "DOWN") setDirection("UP");
      if (newDir === "DOWN" && currentDir !== "UP") setDirection("DOWN");
      if (newDir === "LEFT" && currentDir !== "RIGHT") setDirection("LEFT");
      if (newDir === "RIGHT" && currentDir !== "LEFT") setDirection("RIGHT");
    },
    [gameOver, isPlaying],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (
        [
          "arrowup",
          "arrowdown",
          "arrowleft",
          "arrowright",
          "w",
          "a",
          "s",
          "d",
        ].includes(key)
      ) {
        e.preventDefault();
      }

      let dir: Direction | null = null;
      switch (key) {
        case "arrowup":
        case "w":
          dir = "UP";
          break;
        case "arrowdown":
        case "s":
          dir = "DOWN";
          break;
        case "arrowleft":
        case "a":
          dir = "LEFT";
          break;
        case "arrowright":
        case "d":
          dir = "RIGHT";
          break;
      }

      if (dir) {
        changeDirection(dir);
        setActiveKey(dir);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      let dir: Direction | null = null;
      switch (key) {
        case "arrowup":
        case "w":
          dir = "UP";
          break;
        case "arrowdown":
        case "s":
          dir = "DOWN";
          break;
        case "arrowleft":
        case "a":
          dir = "LEFT";
          break;
        case "arrowright":
        case "d":
          dir = "RIGHT";
          break;
      }
      if (dir) setActiveKey((prev) => (prev === dir ? null : prev));
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [changeDirection]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartX.current || !touchStartY.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (Math.abs(deltaX) > 25)
        deltaX > 0 ? changeDirection("RIGHT") : changeDirection("LEFT");
    } else {
      if (Math.abs(deltaY) > 25)
        deltaY > 0 ? changeDirection("DOWN") : changeDirection("UP");
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const gameLoop = useCallback(() => {
    if (gameOver || !isPlaying) return;

    setSnake((prevSnake) => {
      const head = prevSnake[0];
      const newHead = { ...head };

      switch (currentDirectionRef.current) {
        case "UP":
          newHead.y -= 1;
          break;
        case "DOWN":
          newHead.y += 1;
          break;
        case "LEFT":
          newHead.x -= 1;
          break;
        case "RIGHT":
          newHead.x += 1;
          break;
      }

      if (mode === "WALL") {
        if (
          newHead.x < 0 ||
          newHead.x >= GRID_W ||
          newHead.y < 0 ||
          newHead.y >= GRID_H
        ) {
          setGameOver(true);
          setIsPlaying(false);
          audio.playGameOverSound();
          return prevSnake;
        }
      } else {
        if (newHead.x < 0) newHead.x = GRID_W - 1;
        if (newHead.x >= GRID_W) newHead.x = 0;
        if (newHead.y < 0) newHead.y = GRID_H - 1;
        if (newHead.y >= GRID_H) newHead.y = 0;
      }

      if (prevSnake.some((s) => s.x === newHead.x && s.y === newHead.y)) {
        setGameOver(true);
        setIsPlaying(false);
        audio.playGameOverSound();
        return prevSnake;
      }

      const newSnake = [newHead, ...prevSnake];
      if (newHead.x === food.x && newHead.y === food.y) {
        setScore((s) => s + 10);
        setFood(generateFood(newSnake));
        audio.playEatSound();
      } else {
        newSnake.pop();
      }
      return newSnake;
    });
  }, [food, gameOver, isPlaying, mode]);

  useEffect(() => {
    const intervalId = setInterval(gameLoop, currentDelay);
    return () => clearInterval(intervalId);
  }, [gameLoop, currentDelay]);

  return (
    <div
      className="game-container"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <header className="game-header">
        <div
          className="logo"
          style={{
            fontSize: isPlaying ? "1.5rem" : "2.5rem",
            margin: isPlaying ? "0 auto 0 0" : "auto",
          }}
        >
          SOFTY<span>SNAKE</span>
        </div>
        {isPlaying && (
          <div className="score-readout">
            SCORE: {score.toString().padStart(4, "0")}
          </div>
        )}
      </header>
      <main className="game-area-wrapper">
        <div
          className={`game-area ${mode === "WALL" && isPlaying ? "wall-mode" : ""}`}
        >
          <svg
            viewBox={`0 0 ${VIRTUAL_WIDTH} ${VIRTUAL_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            className="svg-board"
          >
            <defs>
              <filter
                id="neon-glow"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <linearGradient
                id="snake-gradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#00f2fe" />
                <stop offset="100%" stopColor="#4facfe" />
              </linearGradient>
            </defs>

            <rect width="100%" height="100%" fill="rgba(255,255,255,0.02)" />

            <circle
              cx={food.x * CELL_SIZE + 10}
              cy={food.y * CELL_SIZE + 10}
              r={8}
              className="food-item"
            />

            {snake.map((s, i) => {
              const isHead = i === 0;
              return (
                <g key={i}>
                  <rect
                    x={s.x * CELL_SIZE + 1}
                    y={s.y * CELL_SIZE + 1}
                    width={18}
                    height={18}
                    rx={isHead ? 6 : 4}
                    className={isHead ? "snake-head" : "snake-body"}
                  />
                  {isHead && (
                    <>
                      <circle
                        cx={s.x * CELL_SIZE + 6}
                        cy={s.y * CELL_SIZE + 7}
                        r={2}
                        fill="white"
                      />
                      <circle
                        cx={s.x * CELL_SIZE + 14}
                        cy={s.y * CELL_SIZE + 7}
                        r={2}
                        fill="white"
                      />
                    </>
                  )}
                </g>
              );
            })}
          </svg>

          {(!isPlaying || gameOver) && (
            <div className="glass-overlay">
              <div className="mode-selection-container">
                <button
                  className="btn neon-blue-btn"
                  onClick={() => startGame("INFINITE")}
                >
                  INFINITE MODE
                </button>
                <button
                  className="btn neon-red-btn"
                  onClick={() => startGame("WALL")}
                >
                  WALL MODE
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      {isPlaying && (
        <div className="controls-container">
          <div className="d-pad">
            <button
              className={`control-btn up ${activeKey === "UP" ? "active" : ""}`}
              onPointerDown={(e) => {
                e.preventDefault();
                changeDirection("UP");
              }}
            >
              <svg
                className="d-pad-icon"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
            </button>
            <button
              className={`control-btn left ${activeKey === "LEFT" ? "active" : ""}`}
              onPointerDown={(e) => {
                e.preventDefault();
                changeDirection("LEFT");
              }}
            >
              <svg
                className="d-pad-icon"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button
              className={`control-btn right ${activeKey === "RIGHT" ? "active" : ""}`}
              onPointerDown={(e) => {
                e.preventDefault();
                changeDirection("RIGHT");
              }}
            >
              <svg
                className="d-pad-icon"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            <button
              className={`control-btn down ${activeKey === "DOWN" ? "active" : ""}`}
              onPointerDown={(e) => {
                e.preventDefault();
                changeDirection("DOWN");
              }}
            >
              <svg
                className="d-pad-icon"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>
        </div>
      )}
      {isPlaying && (
        <footer className="game-footer">
          <div className="status-bar">
            <span className="mode-tag">{mode} MODE</span>
            <button className="resign-btn" onClick={resignGame}>
              RESIGN SESSION
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default App;
